import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { 
  ArrowBack,
  Brightness7 as Brightness7Icon,
  Brightness4 as Brightness4Icon,
} from '@mui/icons-material';
import axios from 'axios';
import SuccessDialog from '../components/SuccessDialog';
import { getApiUrl } from '../utils/apiUrl';

import MetaTags from '../components/MetaTags';
import { useSharedTheme } from '../theme/useSharedTheme';

const validationSchema = yup.object({
  emailOrUsername: yup
    .string()
    .required('Email or username is required'),
});

const AdminPasswordReset: React.FC = () => {
  const { isDarkMode, toggleTheme, theme } = useSharedTheme();
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      emailOrUsername: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axios.post(`${getApiUrl()}/api/password-reset/request`, {
          emailOrUsername: values.emailOrUsername,
          accountType: 'admin'
        });
        if (response.data.success) {
          setSuccessDialogConfig({
            title: 'Reset Link Sent',
            message: response.data.message || 'Password reset link sent to your email!',
            autoCloseDelay: 5000,
          });
          setShowSuccessDialog(true);
        }
      } catch (error: any) {
        console.error('Password reset error:', error);
        setSuccessDialogConfig({
          title: 'Reset Failed',
          message: error.response?.data?.message || 'Failed to send reset link. Please try again.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <MetaTags 
        title="Admin Password Reset - LeadForge"
        description="Reset your admin password. Enter your email to receive a password reset link."
        keywords="admin password reset, LeadForge, password recovery, admin account"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          background: { 
            xs: 'transparent', 
            md: isDarkMode 
              ? theme.palette.gradients.authDark
              : theme.palette.gradients.authLight
          },
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {/* Centered Form Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            height: { xs: 'auto', md: '100vh' },
            padding: { xs: 0, md: 4 },
            paddingTop: { xs: 0, md: 4 },
          }}
        >
          <Box 
            sx={{
              width: { xs: '100%', md: '60%' }, 
              maxWidth: { xs: 'none', md: 600 }, 
              backgroundColor: isDarkMode ? '#2e2e30' : '#ffffff',
              border: { xs: 'none', md: isDarkMode ? '0.3% solid #B9B9B9' : '0.3px solid #B9B9B9' },
              borderRadius: { xs: 0, md: 4 },
              padding: { xs: '10px', md: 0 },
              marginBottom: { xs: 0, md: 0 },
              boxShadow: { xs: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.1)' },
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: { xs: '100vh', md: '600px' },
              transition: 'background-color 0.3s ease'
            }}
          >


        {/* Second Container - Center: Form Content */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'start', md: 'center' },
          alignItems: 'center',
          flex: { xs: 'none', md: 1 },
          padding: { xs: '10px', md: '20px' },
        }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? '#ffffff' : '#202224',
              mb: 2,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
              letterSpacing: '-0.11px',
            }}
          >
            Admin Password Reset
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#ffffff' : '#666666',
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '0.9rem', md: '16px' },
              letterSpacing: '-0.06px',
              opacity: 0.8,
              maxWidth: '350px',
              lineHeight: 1.4,
            }}
          >
            Enter your admin email or username to<br />receive a password reset link.
          </Typography>

          {/* Form */}
          <Box sx={{ width: '100%', maxWidth: '400px' }}>
            <form onSubmit={formik.handleSubmit}>
              {/* Email Field */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: isDarkMode ? '#ffffff' : '#202224',
                    mb: 1,
                    fontSize: '0.95rem',
                  }}
                >
                  Email Address:
                </Typography>
                <TextField
                  fullWidth
                  id="emailOrUsername"
                  name="emailOrUsername"
                  placeholder="admin@company.com or username"
                  value={formik.values.emailOrUsername}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.emailOrUsername && Boolean(formik.errors.emailOrUsername)}
                  helperText={formik.touched.emailOrUsername && formik.errors.emailOrUsername}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                      border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                      '& fieldset': {
                        border: 'none',
                        display: 'none',
                      },
                      '& input': {
                        color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                        fontSize: '1rem',
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                      },
                      '& input:-webkit-autofill': {
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                        WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f8fafc'} inset`,
                        WebkitTextFillColor: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                      },
                      '& input:-webkit-autofill:hover': {
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                        WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f1f5f9'} inset`,
                      },
                      '& input:-webkit-autofill:focus': {
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                        WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#ffffff'} inset`,
                      },
                      '& input::placeholder': {
                        color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                        opacity: '1',
                      },
                      '&:hover': {
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                        border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                        '& fieldset': {
                          border: 'none',
                          display: 'none',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                        border: '1px solid #6052A2',
                        boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                        '& fieldset': {
                          border: 'none',
                          display: 'none',
                        },
                      },
                    },
                  }}
                />
              </Box>

              {/* Send Reset Link Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    px: 6,
                    borderRadius: 2,
                    background: isDarkMode 
                      ? '#ffffff' 
                      : '#1B4672',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isDarkMode ? '#1B4672' : 'white',
                    '&:hover': {
                      background: isDarkMode 
                        ? '#f0f0f0' 
                        : '#1B4672',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </Box>
            </form>

            {/* Back to Admin Login */}
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                mt: 2,
                color: isDarkMode ? '#ffffff' : '#666666',
              }}
            >
              <Link
                href="/admin/login"
                sx={{
                  color: isDarkMode ? '#ffffff' : '#1B4672',
                  textDecoration: 'underline',
                  fontWeight: 500,
                  fontSize: '16px',
                  '&:hover': {
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                  },
                }}
              >
                Back to Admin Login
              </Link>
            </Typography>
          </Box>
        </Box>

            {/* Third Container - Bottom: Account Link */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '10px', md: '20px' },
            }}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: isDarkMode ? '#ffffff' : '#666666',
                  fontSize: '0.85rem',
                }}
              >
                Don't have an account?{' '}
                <Link
                  href="/admin-signup"
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontSize: '16px',
                    '&:hover': {
                      color: isDarkMode ? '#ffffff' : '#1B4672',
                    },
                  }}
                >
                  Request Access
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </ThemeProvider>
  );
};

export default AdminPasswordReset;